import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { ApiError, apiFetch, apiJson } from '../../lib/api'
import { PageBody, PageHeader } from '../../components/PageHeader'
import { CardsPreview, CardsResponse, dateText } from './api'
import styles from './GodMode.module.css'

// Printed worker cards for one client's people: each card names its person
// by their card number, so a receipter scanning it credits them.
export function WorkerCardsPage() {
  const { id = '' } = useParams()
  const { user } = useAuth()
  const [data, setData] = useState<CardsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [chosen, setChosen] = useState<string[]>([])
  const [perPerson, setPerPerson] = useState('1')
  const [start, setStart] = useState('')
  const [width, setWidth] = useState('4')
  const [alphabet, setAlphabet] = useState('alphanumeric')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [made, setMade] = useState<string | null>(null)
  const [preview, setPreview] = useState<CardsPreview | null>(null)
  const base = `/api/god-mode/clients/${id}/cards`

  const load = useCallback(async () => {
    try {
      setData(await apiJson<CardsResponse>(user, base))
    } catch (e: any) {
      setError(e.message)
    }
  }, [user, base])

  useEffect(() => {
    load()
  }, [load])

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase()
    const people = data?.people ?? []
    if (!q) return people
    return people.filter((p) => [p.name, p.employee_id, p.card_number].some((v) => v != null && String(v).toLowerCase().includes(q)))
  }, [data, search])

  const ask = () => ({
    human_ids: chosen,
    per_person: Number(perPerson),
    start: start.trim() === '' ? null : start.trim().toUpperCase(),
    width: Number(width),
    alphabet,
  })
  // Any change to what is asked for means previewing again.
  const edit = (set: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    set(e.target.value)
    setPreview(null)
  }
  const toggle = (ids: string[], on: boolean) => {
    setChosen((was) => (on ? [...was, ...ids.filter((x) => !was.includes(x))] : was.filter((x) => !ids.includes(x))))
    setPreview(null)
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setMade(null)
    try {
      setPreview(await apiJson<CardsPreview>(user, `${base}/preview`, { method: 'POST', body: ask() }))
    } catch (err: any) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const make = async () => {
    if (!preview) return
    setBusy(true)
    setError(null)
    try {
      const r = await apiJson<CardsPreview>(user, base, {
        method: 'POST',
        body: { ...ask(), note: note || null, expect: { count: preview.count, first: preview.first, last: preview.last } },
      })
      setMade(`Made ${r.count} cards, ${r.first} to ${r.last}.`)
      setPreview(null)
      setChosen([])
      setStart('')
      setNote('')
      await load()
    } catch (err: any) {
      if (err instanceof ApiError && err.body?.code === 'STOCK_CHANGED' && err.body.preview) setPreview(err.body.preview)
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const download = async (batchId: string) => {
    setError(null)
    try {
      const res = await apiFetch(user, `${base}/${batchId}/csv`)
      if (!res.ok) throw new Error('The sheet could not be downloaded. Try again.')
      const url = URL.createObjectURL(await res.blob())
      const a = document.createElement('a')
      a.href = url
      a.download = `dataphyll-cards-${batchId.slice(0, 8)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      setError(err.message)
    }
  }

  const row = (r: CardsPreview['head'][number]) => (
    <tr key={r.unique}>
      <td>{r.unique}</td>
      <td>{r.qr ? <code>{r.qr}</code> : 'Numbered when made'}</td>
      <td>{r.name}</td>
    </tr>
  )
  const allShown = shown.length > 0 && shown.every((p) => chosen.includes(p.id))

  return (
    <>
      <PageHeader title={data ? `Worker cards · ${data.client.name}` : 'Worker cards'} />
      <PageBody>
        <div className={styles.stack}>
          <div>
            <Link to={`/god-mode/clients/${id}`} className={styles.secondary}>
              ← Back to client
            </Link>
          </div>
          {error && <p className={styles.error}>{error}</p>}
          {!data && !error && <p className={styles.lead}>Loading…</p>}
          {data && (
            <>
              <form className={`${styles.card} ${styles.form}`} onSubmit={submit}>
                <h2>New batch</h2>
                {made && <p className={styles.saved}>{made}</p>}
                <label className={styles.field}>
                  People
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, employee ID or card number" />
                </label>
                <div className={styles.tableCard}>
                  {data.people.length === 0 ? (
                    <div className={styles.empty}>This client has no people yet.</div>
                  ) : (
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>
                            <input type="checkbox" aria-label="Choose everyone shown" checked={allShown} onChange={(e) => toggle(shown.map((p) => p.id), e.target.checked)} />
                          </th>
                          <th>Name</th>
                          <th>Employee ID</th>
                          <th>Card number</th>
                          <th>Cards</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shown.map((p) => (
                          <tr key={p.id}>
                            <td>
                              <input type="checkbox" aria-label={`Choose ${p.name}`} checked={chosen.includes(p.id)} onChange={(e) => toggle([p.id], e.target.checked)} />
                            </td>
                            <td>{p.name}</td>
                            <td>{p.employee_id || '—'}</td>
                            <td>{p.card_number ?? 'None yet'}</td>
                            <td>{p.cards}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className={styles.inline}>
                  <label className={styles.field}>
                    Cards each
                    <input type="number" min={1} max={data.per_person_max} value={perPerson} onChange={edit(setPerPerson)} required />
                  </label>
                  <label className={styles.field}>
                    Count in
                    <select value={alphabet} onChange={edit(setAlphabet)}>
                      <option value="alphanumeric">Letters and numbers</option>
                      <option value="numeric">Numbers only</option>
                    </select>
                  </label>
                  <label className={styles.field}>
                    First ID
                    <input value={start} onChange={edit(setStart)} placeholder="Next free" maxLength={12} />
                  </label>
                  <label className={styles.field}>
                    Characters
                    <input type="number" min={1} max={12} value={width} onChange={edit(setWidth)} required />
                  </label>
                </div>
                <label className={styles.field}>
                  Note
                  <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="e.g. supplier order, season" />
                  <span className={styles.hint}>
                    Card IDs count on across the people chosen, in the order shown. Someone without a card number is given one, unique across
                    Dataphyll, when the cards are made. A receipter scanning a card credits its person; a picker cannot scan one. The sheet has the
                    supplier's columns: Unique DATA, QR DATA.
                  </span>
                </label>
                {preview && (
                  <div aria-label="Preview">
                    <p className={styles.lead}>
                      {preview.count === 0
                        ? 'No cards to make.'
                        : `${preview.count} cards for ${preview.people.length} ${preview.people.length === 1 ? 'person' : 'people'}, ${preview.first} to ${preview.last}.`}
                      {preview.skipped.length > 0 &&
                        ` Passes over ${preview.skipped.length} already made: ${preview.skipped.slice(0, 10).join(', ')}${preview.skipped.length > 10 ? '…' : ''}.`}
                    </p>
                    {preview.count > 0 && (
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th>Unique DATA</th>
                            <th>QR DATA</th>
                            <th>Person</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.head.map(row)}
                          {preview.count > preview.head.length + preview.tail.length && (
                            <tr>
                              <td colSpan={3}>… {preview.count - preview.head.length - preview.tail.length} more …</td>
                            </tr>
                          )}
                          {preview.tail.map(row)}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
                <div className={styles.inline}>
                  <button type="submit" className={preview ? styles.secondary : styles.primary} disabled={busy || chosen.length === 0}>
                    {preview ? 'Preview again' : busy ? 'Checking…' : `Preview${chosen.length ? ` for ${chosen.length}` : ''}`}
                  </button>
                  {preview && preview.count > 0 && (
                    <button type="button" className={styles.primary} disabled={busy} onClick={make}>
                      {busy ? 'Making…' : `Make ${preview.count} cards`}
                    </button>
                  )}
                </div>
              </form>

              <div className={styles.tableCard}>
                {data.batches.length === 0 ? (
                  <div className={styles.empty}>No cards made for this client yet.</div>
                ) : (
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Made</th>
                        <th>Cards</th>
                        <th>Count</th>
                        <th>People</th>
                        <th>Note</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.batches.map((b) => (
                        <tr key={b.batch_id}>
                          <td title={b.created_by || undefined}>{dateText(b.created_at)}</td>
                          <td>
                            {b.first}–{b.last}
                          </td>
                          <td>{b.count}</td>
                          <td title={b.names.join(', ')}>
                            {b.names.slice(0, 3).join(', ')}
                            {b.people > 3 ? ` and ${b.people - 3} more` : ''}
                          </td>
                          <td>{b.note || '—'}</td>
                          <td>
                            <button type="button" className={styles.secondary} onClick={() => download(b.batch_id)}>
                              Download sheet
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </PageBody>
    </>
  )
}
